// Air-to-air gun lead solution, shared by the HUD lead pip (main.js) and its
// regression test (tools/check_gun_lead.mjs) so the two can never drift.
//
// The server fires plane rounds at  Vel = shooter.Vel + dir*muzzle  and then
// integrates them under gravity (internal/game/world.go: fireGun, stepBullets).
// To put a round on a moving target we therefore have to solve for two things:
//
//   1. Deflection — because the round carries the shooter's own velocity, the
//      lead is built from the CLOSING (relative) velocity, target motion minus
//      our own, not the target's world-frame motion alone. (Leading by the full
//      target velocity biases the aim by ~ own-speed x time-of-flight.)
//   2. Drop — the linear intercept ignores gravity, so over the time of flight
//      the round falls 0.5*g*t^2. We raise the aim point by that much so the
//      round arcs back down onto the lead point.
//
// The result is the world point to lay the pipper on; pointing the guns there
// makes the tracer stream converge on the target.

import { GRAVITY } from "./fdm.js";

/**
 * World-space aim point (deflection + gravity drop) for a forward-firing gun.
 *
 * @param selfPos    shooter position {x,y,z}
 * @param selfVel    shooter velocity {x,y,z} (rounds inherit this)
 * @param tgtPos     target position {x,y,z}
 * @param tgtVel     target velocity {x,y,z}
 * @param muzzleMS   muzzle speed (m/s) of the gun's caliber
 * @param out        optional {x,y,z} to write into (avoids per-frame allocation)
 * @param gravity    gravity (m/s^2); defaults to the shared flight-model value
 * @returns the out object (or a fresh one) with the aim point
 */
export function gunLeadPoint(selfPos, selfVel, tgtPos, tgtVel, muzzleMS, out = {}, gravity = GRAVITY) {
  const relX = tgtPos.x - selfPos.x;
  const relY = tgtPos.y - selfPos.y;
  const relZ = tgtPos.z - selfPos.z;
  const dist = Math.hypot(relX, relY, relZ) || 1e-6;

  // Closing (relative) velocity — target motion minus our own.
  const rvx = tgtVel.x - selfVel.x;
  const rvy = tgtVel.y - selfVel.y;
  const rvz = tgtVel.z - selfVel.z;

  // Range rate (+ = opening); a closing pass shortens the time of flight.
  const rangeRate = (rvx * relX + rvy * relY + rvz * relZ) / dist;
  const closing = Math.max(60, muzzleMS - rangeRate);
  const tof = dist / closing;

  // Deflection: lead by the closing velocity. Drop: raise by the fall the round
  // accrues over the flight so it lands back on the lead point.
  out.x = tgtPos.x + rvx * tof;
  out.y = tgtPos.y + rvy * tof + 0.5 * gravity * tof * tof;
  out.z = tgtPos.z + rvz * tof;
  return out;
}

/**
 * World-space point a centered (no-deflection) burst actually reaches: the
 * wing-gun harmonization (convergence) point, dropped by the gravity fall the
 * rounds take getting there. Used to range the boresight gun reticle so the
 * glyph sits on the true point of impact instead of the bore line.
 *
 * @param selfPos      shooter position {x,y,z}
 * @param selfVel      shooter velocity {x,y,z} (rounds inherit this)
 * @param fwd          unit nose/bore direction {x,y,z}
 * @param convergenceM harmonization range (m) where the wing guns cross
 * @param muzzleMS     muzzle speed (m/s) of the gun's caliber
 * @param out          optional {x,y,z} to write into
 * @param gravity      gravity (m/s^2); defaults to the shared flight-model value
 * @returns the out object (or a fresh one) with the reticle point
 */
export function gunBoresightReticle(selfPos, selfVel, fwd, convergenceM, muzzleMS, out = {}, gravity = GRAVITY) {
  // Rounds reach the convergence range sooner for the speed we throw at them
  // (the airframe's forward velocity adds to the muzzle speed), so less drop.
  const fwdSpeed = Math.max(0, fwd.x * selfVel.x + fwd.y * selfVel.y + fwd.z * selfVel.z);
  const tof = convergenceM / (muzzleMS + fwdSpeed);
  const drop = 0.5 * gravity * tof * tof;
  out.x = selfPos.x + fwd.x * convergenceM;
  out.y = selfPos.y + fwd.y * convergenceM - drop; // gravity falls in world -Y
  out.z = selfPos.z + fwd.z * convergenceM;
  return out;
}
